import { useEffect, useMemo, useState } from 'react';
import { Map as ImmutableMap } from 'immutable';
import AsyncData from 'conversations-async-data/async-data/AsyncData';
import userInfo from 'hub-http/userInfo';
import { gate } from 'hub-http/gates';
import CallNumbersPopover from './CallNumbersPopover';
import CallWorkspacePopover from './CallWorkspacePopover';
import CallNumbersActivityCreatorPopover from './CallNumbersActivityCreatorPopover';
import CallNumbersOnboardedWindowPopover from './CallNumbersOnboardedWindowPopover';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { isFailed, isLoading, isUninitialized } from 'conversations-async-data/async-data/operators/statusComparators';
import { FAILED, SUCCEEDED } from 'conversations-async-data/async-data/constants/asyncStatuses';
import { logNetworkError } from 'calling-lifecycle-internal/utils/logNetworkError';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import CalleeError from './callee-error/CalleeError';
import { CALLING_CRM_RECORDS_REMOTE } from 'calling-internal-common/constants/CallingGates';
import { PROFILE, HELP_DESK, INBOX, REFERENCE_UI, ACTIVITY_CREATOR, SHARED_DEAL_CARD, GUIDED_EXECUTION } from 'calling-internal-common/constants/appIdentifiers';
import { CHANGE_CALL_PROVIDER } from 'calling-global-api/constants/EventListeners';
import setCallingProvidersSetting from 'calling-global-api/lib/setCallingProvidersSetting';
import { addEventListener, removeEventListener } from 'calling-global-api/event-registration/addEventListener';
import { getPersistedFromNumber } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { getIsProviderHubSpot } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import { getIsTwilioBasedCallProvider } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import { CONNECT_FROM_NUMBER } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { FROM_NUMBER } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { jsx as _jsx } from "react/jsx-runtime";
function getAppIdentifierFromUrl(override) {
  const url = window.location.pathname;
  if (override) {
    return override;
  } else {
    return url.includes('/record/') ? PROFILE : url.includes('/help-desk/') ? HELP_DESK : url.includes('/inbox/') ? INBOX : url.includes('/cv-calling-integration/') ? REFERENCE_UI : url.includes('/prospecting/') ? GUIDED_EXECUTION : ACTIVITY_CREATOR;
  }
}
function isHelpdeskComponent(appIdentifierFromUrl, props) {
  return appIdentifierFromUrl === HELP_DESK;
}
function isInboxComponent(appIdentifierFromUrl, props) {
  return appIdentifierFromUrl === INBOX;
}
function isActivityCreatorComponent(appIdentifierFromUrl, props) {
  return appIdentifierFromUrl === ACTIVITY_CREATOR || appIdentifierFromUrl === SHARED_DEAL_CARD;
}
function isCrmRecordsComponent(appIdentifierFromUrl, props) {
  return appIdentifierFromUrl === PROFILE;
}
function isOnboardedWindowPopover(appIdentifierFromUrl, props) {
  return appIdentifierFromUrl === GUIDED_EXECUTION;
}
const CALLING_REDESIGN = gate('Calling:Rep:Redesign');
const useCallNumbersPopover = props => {
  const [auth, setAuth] = useState(new AsyncData());
  const {
    objectTypeId,
    onCallButtonClick,
    shouldFetch,
    subjectId,
    visitorPhoneNumber,
    isKnownVisitor,
    appIdOverride,
    onLegacyOutboundCallStart
  } = props;
  const appIdentifierFromUrl = getAppIdentifierFromUrl(appIdOverride);
  useEffect(() => {
    // Only fetch if popover is open and auth has not been initialized
    if (!shouldFetch || !isUninitialized(auth)) {
      return;
    }
    userInfo().then(data => {
      setAuth(new AsyncData({
        status: SUCCEEDED,
        data
      }));
    }).catch(networkError => {
      setAuth(new AsyncData({
        status: FAILED,
        error: networkError
      }));
      logNetworkError({
        error: new Error('Failed to fetch user info'),
        networkError
      });
    });
  }, [shouldFetch, auth]);
  const handleChangeCallProvider = ({
    callingProvider
  }) => {
    let fromNumber = null;
    if (getIsTwilioBasedCallProvider(callingProvider)) {
      const fromNumberKey = getIsProviderHubSpot(callingProvider) ? FROM_NUMBER : CONNECT_FROM_NUMBER;
      fromNumber = getPersistedFromNumber(fromNumberKey);
    }
    setCallingProvidersSetting({
      callingProvider,
      fromNumber
    });
  };
  useEffect(() => {
    addEventListener(CHANGE_CALL_PROVIDER, handleChangeCallProvider);
    return () => {
      removeEventListener(CHANGE_CALL_PROVIDER, handleChangeCallProvider);
    };
  }, []);
  const renderedCallNumbersPopover = useMemo(() => {
    if (isLoading(auth)) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered",
        minHeight: 100,
        "data-test-id": "call-numbers-popover-loading-spinner"
      });
    }
    if (isFailed(auth)) {
      return /*#__PURE__*/_jsx(CalleeError, {
        "data-test-id": "call-numbers-popover-callee-error"
      });
    }
    const {
      user,
      portal,
      gates
    } = getData(auth);
    // Convert scopes to ImmutableMap friendly (ie. ['scope', true])
    const scopes = user.scopes.reduce((acc, scope) => {
      acc[scope] = true;
      return acc;
    }, {});
    const isHelpdesk = isHelpdeskComponent(appIdentifierFromUrl, props);
    const isInbox = isInboxComponent(appIdentifierFromUrl, props);
    const isActivityCreator = isActivityCreatorComponent(appIdentifierFromUrl, props);
    const isCrmRecords = isCrmRecordsComponent(appIdentifierFromUrl, props);
    const isOnboardedWindow = isOnboardedWindowPopover(appIdentifierFromUrl, props);
    const isUngatedForRedesign = !!gates && gates.includes(CALLING_REDESIGN);
    const isUngatedForCrmRecordsRemote = !!gates && gates.includes(CALLING_CRM_RECORDS_REMOTE);
    return isActivityCreator || isCrmRecords && isUngatedForCrmRecordsRemote ? /*#__PURE__*/_jsx(CallNumbersActivityCreatorPopover, {
      appIdentifier: appIdentifierFromUrl,
      limits: ImmutableMap(portal.limits),
      objectTypeId: objectTypeId,
      onCallButtonClick: onCallButtonClick,
      "data-test-id": "call-numbers-popover",
      scopes: ImmutableMap(scopes),
      subjectId: subjectId,
      isUngatedForRedesign: isUngatedForRedesign,
      gates: gates
    }) : isHelpdesk || isInbox ? /*#__PURE__*/_jsx(CallWorkspacePopover, {
      appIdentifier: appIdentifierFromUrl,
      "data-test-id": "call-numbers-popover",
      gates: gates,
      limits: ImmutableMap(portal.limits),
      objectTypeId: objectTypeId,
      onCallButtonClick: onCallButtonClick,
      scopes: ImmutableMap(scopes),
      subjectId: subjectId,
      defaultFromNumber: props.defaultFromNumber,
      visitorPhoneNumber: visitorPhoneNumber,
      isKnownVisitor: isKnownVisitor,
      ticketId: props.ticketId,
      threadId: props.threadId,
      isUngatedForRedesign: isUngatedForRedesign
    }) : isOnboardedWindow ? /*#__PURE__*/_jsx(CallNumbersOnboardedWindowPopover, {
      appIdentifier: appIdentifierFromUrl,
      limits: ImmutableMap(portal.limits),
      objectTypeId: objectTypeId,
      onCallButtonClick: onCallButtonClick,
      onLegacyOutboundCallStart: onLegacyOutboundCallStart,
      "data-test-id": "call-numbers-popover",
      scopes: ImmutableMap(scopes),
      subjectId: subjectId,
      prospectingQueueItemId: props.prospectingQueueItemId,
      isUngatedForRedesign: isUngatedForRedesign,
      gates: gates
    }) : /*#__PURE__*/_jsx(CallNumbersPopover, {
      subjectId: subjectId,
      objectTypeId: objectTypeId,
      onCallButtonClick: onCallButtonClick,
      scopes: ImmutableMap(scopes),
      limits: ImmutableMap(portal.limits),
      gates: gates,
      appIdentifier: appIdentifierFromUrl,
      "data-test-id": "call-numbers-popover"
    });
  }, [appIdentifierFromUrl, auth, isKnownVisitor, objectTypeId, onCallButtonClick, props, subjectId, visitorPhoneNumber, onLegacyOutboundCallStart]);
  return [renderedCallNumbersPopover];
};
export default useCallNumbersPopover;